import { useEffect, useRef, useState } from "react";

import type { ISite } from "@similarweb/ui-components";

import { BLOCK_SITE_STATUS } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketingHelper";
import { ListItemWebsitePartialSelectionStyled } from "pro-features/components/AutocompleteWebsites/styles";
import { filterOutIllegalWebsiteItems } from "pro-features/components/AutocompleteWebsites/utils";
import { translate } from "app/filters/i18nFilter";
import type { PrimaryWebsite } from "app/pages/activation/components/SetupModal/types";
import { AutocompleteContent } from "app/pages/activation/components/Shared/AutocompleteWebsitesWithKeysControl/AutocompleteContent";
import type { AutocompleteWebsitesWithKeysControlSchema } from "app/pages/activation/components/Shared/AutocompleteWebsitesWithKeysControl/AutocompleteWebsitesWithKeysControl";
import {
    INITIAL_SELECTED_INDEX,
    MAX_RESULTS,
    SCROLL_GAP,
    SCROLL_STEP,
} from "app/pages/activation/components/Shared/AutocompleteWebsitesWithKeysControl/config";
import {
    RECENT_AUTOCOMPLETE,
    SUGGESTED_AUTOCOMPLETE,
} from "app/pages/activation/components/Shared/AutocompleteWebsitesWithKeysControl/texts";
import { RECENT_ITEMS_TYPE } from "app/pages/activation/helpers/getRecentItems";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

type UseAutocompleteWithKeysSchema = Pick<
    AutocompleteWebsitesWithKeysControlSchema,
    "renderOnEmptyQuery" | "autocompleteRef" | "exclude" | "onItemClick" | "height" | "maxResults"
>;

export const useAutocompleteWithKeys = ({
    renderOnEmptyQuery,
    exclude,
    onItemClick,
    height,
    autocompleteRef,
    maxResults,
}: UseAutocompleteWithKeysSchema) => {
    const [selectedIndex, setSelectedIndex] = useState(INITIAL_SELECTED_INDEX);
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);

    const listItems = useRef<ISite[]>([]);

    const onAutocompleteGetData = async (query: string) => {
        if (query === "") {
            const recentItems = renderOnEmptyQuery?.() ?? [];
            listItems.current = recentItems;

            return recentItems;
        } else {
            setSelectedIndex(INITIAL_SELECTED_INDEX);
            setAutocompleteLoading(true);
            const results = await getWebsiteResults(query);
            setAutocompleteLoading(false);

            const filteredItems = results.filter(
                (item) =>
                    !exclude.includes(item.name) &&
                    !item.blockStatus?.includes(BLOCK_SITE_STATUS) &&
                    filterOutIllegalWebsiteItems(item),
            );

            listItems.current = filteredItems;

            return filteredItems;
        }
    };

    const onEnterKeyPress = (event: KeyboardEvent, obj: PrimaryWebsite) => {
        event.stopPropagation();

        onItemClick(obj);
        setSelectedIndex(INITIAL_SELECTED_INDEX);

        autocompleteRef.current?.onCloseButtonClick();
    };

    const itemCreator = (item: PrimaryWebsite, index: number, onClick: (item: PrimaryWebsite) => void) => {
        const { name, icon, image } = item;
        const isActive = index === selectedIndex;

        return (
            <ListItemWebsitePartialSelectionStyled
                key={name}
                isActive={isActive}
                text={name}
                onClick={() => onClick(item)}
                img={icon ?? image}
            />
        );
    };

    const getRenderItems = (onItemClick: (item: PrimaryWebsite) => void) =>
        function ({ listItems }) {
            const className = listItems.length > 0 ? "ListItemsContainer" : "";
            const recents = [];
            const searchResults = [];

            const handleItemClick = (item: PrimaryWebsite) => {
                onItemClick(item);
                setSelectedIndex(INITIAL_SELECTED_INDEX);
            };

            [...listItems].map((item, index) => {
                if (item.type === RECENT_ITEMS_TYPE) {
                    recents.push(itemCreator(item, index, handleItemClick));
                } else {
                    searchResults.push(itemCreator(item, index, handleItemClick));
                }
            });

            if (!listItems.length) {
                return null;
            }

            return (
                <div className={className}>
                    {recents.length > 0 ? (
                        <AutocompleteContent
                            data={recents}
                            height={height}
                            separatorText={translate(RECENT_AUTOCOMPLETE)}
                            className="autocomplete-scrollarea"
                            dataAutomation="automation-list-separator"
                        />
                    ) : null}

                    {searchResults.length > 0 ? (
                        <AutocompleteContent
                            data={searchResults}
                            height={height}
                            separatorText={translate(SUGGESTED_AUTOCOMPLETE)}
                            className="autocomplete-scrollarea"
                            dataAutomation="automation-list-suggested"
                        />
                    ) : null}
                </div>
            );
        };

    const getScrollPosition = (index: number, scrollArea: Element) => {
        if (!scrollArea) {
            return 0;
        }

        const scrollAreaHeight = scrollArea.clientHeight;
        const itemTotalHeight = (index + 1) * SCROLL_STEP;
        const scrollTop = scrollArea.scrollTop || 0;

        if (itemTotalHeight + SCROLL_GAP > scrollTop + scrollAreaHeight) {
            return itemTotalHeight - scrollAreaHeight;
        }

        if (itemTotalHeight - SCROLL_STEP < scrollTop + SCROLL_GAP) {
            return itemTotalHeight - SCROLL_STEP;
        }

        return scrollTop;
    };

    const onArrowKeyPress = (event: KeyboardEvent) => {
        const { key } = event;
        const items = listItems.current.slice(0, maxResults ?? MAX_RESULTS);
        const maxIndex = items.length - 1;

        if (key === "ArrowDown") {
            setSelectedIndex((prevIndex) => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
        }
        if (key === "ArrowUp") {
            setSelectedIndex((prevIndex) => (prevIndex <= 0 ? maxIndex : prevIndex - 1));
        }
    };

    useEffect(() => {
        const scrollArea = document.querySelector(".autocomplete-scrollarea");

        if (scrollArea) {
            const newScrollPosition = getScrollPosition(selectedIndex, scrollArea);

            scrollArea.scrollTo({ top: newScrollPosition, behavior: "smooth" });
        }
    }, [selectedIndex]);

    return {
        onAutocompleteGetData,
        onEnterKeyPress,
        itemCreator,
        getRenderItems,
        onArrowKeyPress,
        autocompleteLoading,
        selectedIndex,
    };
};
